import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Phone, Clock, Shield, DollarSign, Check, Star, MapPin, ArrowRight } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { FAQSection } from '../components/FAQSection';
import toast from 'react-hot-toast';
import { ImageWithFallback } from '../components/ImageWithFallback';

interface Service {
  id: string;
  title: string;
  slug: string;
  description: string;
  image_url: string;
  features: string[];
}
import { CitySearch } from '../components/CitySearch';
import { SchemaMarkup } from '../components/SchemaMarkup';
import { trackLead } from '../utils/analytics';

export function ServicePage() {
  const { id } = useParams();
  const [service, setService] = useState<Service | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [faqs, setFaqs] = useState<any[]>([]);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    zipCode: '',
    message: ''
  });

  useEffect(() => {
    async function loadService() {
      try {
        if (!id || id === 'undefined') {
          setError('Service not found');
          setLoading(false);
          return;
        }

        const { data, error } = await supabase
          .from('services')
          .select('*')
          .eq('slug', id)
          .single();

        if (error) throw error;
        if (!data) throw new Error('Service not found');

        // Validate required fields
        if (!data.title || !data.slug) {
          throw new Error('Invalid service data');
        }

        setService(data);
        setError(null);
        // Load FAQs for this service
        const { data: faqsData, error: faqsError } = await supabase
          .from('faqs')
          .select('*')
          .eq('service_id', data.id)
          .order('order');
        
        if (faqsError) throw faqsError;
        

        setError(null);
        setFaqs(faqsData || []);

      } catch (error) {
        console.error('Error loading service:', error);
        const message = error instanceof Error ? error.message : 'Service not found';
        setError(message);
        toast.error(message);
      } finally {
        setLoading(false);
      }
    }

    loadService();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl text-gray-600">Loading service details...</div>
      </div>
    );
  }

  if (!service) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Service Not Found</h2>
          <p className="text-gray-600 mb-8">{error || 'The requested service could not be found.'}</p>
          <Link 
            to="/services" 
            className="inline-flex items-center px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            View All Services
          </Link>
        </div>
      </div>
    );
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Track lead
    trackLead('quote_request', {
      service_id: service.id,
      ...formData
    });
    
    // Clear form
    setFormData({
      name: '',
      phone: '',
      email: '',
      zipCode: '',
      message: ''
    });

    // Show success message
    toast.success('Quote request submitted successfully!');
  };

  return (
    <>
      <Helmet>
        <title>{`${service.title} | Professional Plumbing Services - NearPlumbers`}</title>
        <meta 
          name="description" 
          content={`Professional ${service.title.toLowerCase()} services. Available 24/7 for emergencies. Licensed & insured plumbers, upfront pricing.`} 
        />
        <meta 
          name="keywords" 
          content={`${service.title.toLowerCase()}, emergency plumber, plumbing service, licensed plumber, ${service.features.join(', ')}`} 
        />
        <link rel="canonical" href={`https://nearplumbers.com/services/${id}`} />
      </Helmet>

      <div className="bg-white">
        <div className="relative bg-gradient-to-r from-blue-700 to-blue-900">
          <ImageWithFallback
            src={service.image_url}
            alt={service.title}
            className="w-full h-[500px] object-cover opacity-30"
            sizes="100vw"
          />
          <div className="absolute bottom-0 left-0 right-0 p-8">
            <div className="max-w-7xl mx-auto">
              <div className="max-w-3xl">
                <h1 className="text-5xl font-bold text-white mb-4">{service.title}</h1>
                <p className="text-xl text-gray-200 mb-8">{service.description}</p>
                <div className="flex items-center space-x-4">
                  <a
                    href="tel:(855) 513-0871"
                    className="inline-flex items-center px-6 py-3 bg-white text-blue-600 rounded-full text-lg font-semibold hover:bg-blue-50 transition-colors"
                  >
                    <Phone className="w-5 h-5 mr-2" />
                    Call Now
                  </a>
                  <button
                    onClick={() => document.getElementById('quote-form')?.scrollIntoView({ behavior: 'smooth' })}
                    className="inline-flex items-center px-6 py-3 bg-transparent border-2 border-white text-white rounded-full text-lg font-semibold hover:bg-white hover:text-blue-600 transition-colors"
                  >
                    Get Quote
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto py-16 px-4 sm:px-6 lg:px-8">
          {/* Service Features */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            {service.features.map((feature, index) => (
              <div key={index} className="bg-gray-50 p-6 rounded-lg">
                <Check className="w-6 h-6 text-green-500 mb-4" />
                <h3 className="text-lg font-semibold mb-2">{feature}</h3>
                <p className="text-gray-600">Professional service guaranteed</p>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-8">Expert {service.title}</h2>
              <div className="space-y-6">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <Clock className="h-8 w-8 text-blue-600" />
                  </div>
                  <div className="ml-4">
                    <h3 className="text-xl font-semibold">Available 24/7</h3>
                    <p className="mt-2 text-gray-600">Emergency service available around the clock. Our team is ready to help whenever you need us.</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <Shield className="h-8 w-8 text-blue-600" />
                  </div>
                  <div className="ml-4">
                    <h3 className="text-xl font-semibold">Licensed & Insured</h3>
                    <p className="mt-2 text-gray-600">All our plumbers are fully licensed, insured, and background checked for your peace of mind.</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <DollarSign className="h-8 w-8 text-blue-600" />
                  </div>
                  <div className="ml-4">
                    <h3 className="text-xl font-semibold">Upfront Pricing</h3>
                    <p className="mt-2 text-gray-600">Clear, upfront pricing with no hidden fees. Get a detailed quote before we begin work.</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <Star className="h-8 w-8 text-blue-600" />
                  </div>
                  <div className="ml-4">
                    <h3 className="text-xl font-semibold">Satisfaction Guaranteed</h3>
                    <p className="mt-2 text-gray-600">We stand behind our work with a 100% satisfaction guarantee on all services.</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 p-8 rounded-lg" id="quote-form">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Get a Free Quote</h2>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700">Name</label>
                  <input
                    type="text"
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700">Phone</label>
                  <input
                    type="tel"
                    id="phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700">Email</label>
                  <input
                    type="email"
                    id="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="zipCode" className="block text-sm font-medium text-gray-700">ZIP Code</label>
                  <input
                    type="text"
                    id="zipCode"
                    value={formData.zipCode}
                    onChange={(e) => setFormData({ ...formData, zipCode: e.target.value })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700">Message</label>
                  <textarea
                    id="message"
                    rows={4}
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    placeholder="Tell us about your plumbing needs..."
                  ></textarea>
                </div>
                <button
                  type="submit"
                  className="w-full bg-blue-600 text-white py-3 px-6 rounded-md hover:bg-blue-700 flex items-center justify-center transition-colors"
                >
                  <Phone className="w-5 h-5 mr-2" />
                  Get Free Quote
                </button>
                <p className="text-sm text-gray-500 text-center mt-4">
                  No obligation quote • Available 24/7
                </p>
              </form>
            </div>
          </div>

          {/* Service Areas */}
          <div className="mt-24">
            <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">Find a Plumber Near You</h2>
            <div className="max-w-xl mx-auto">
              <CitySearch />
            </div>
            <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-6">
              <Link to="/ny/new-york/10001" className="group">
                <div className="bg-gray-50 p-6 rounded-lg hover:bg-blue-50 transition-colors">
                  <MapPin className="w-6 h-6 text-blue-600 mb-2" />
                  <h3 className="font-semibold group-hover:text-blue-600">New York, NY</h3>
                </div>
              </Link>
              <Link to="/ca/los-angeles/90001" className="group">
                <div className="bg-gray-50 p-6 rounded-lg hover:bg-blue-50 transition-colors">
                  <MapPin className="w-6 h-6 text-blue-600 mb-2" />
                  <h3 className="font-semibold group-hover:text-blue-600">Los Angeles, CA</h3>
                </div>
              </Link>
              <Link to="/il/chicago/60601" className="group">
                <div className="bg-gray-50 p-6 rounded-lg hover:bg-blue-50 transition-colors">
                  <MapPin className="w-6 h-6 text-blue-600 mb-2" />
                  <h3 className="font-semibold group-hover:text-blue-600">Chicago, IL</h3>
                </div>
              </Link>
              <Link to="/tx/houston/77001" className="group">
                <div className="bg-gray-50 p-6 rounded-lg hover:bg-blue-50 transition-colors">
                  <MapPin className="w-6 h-6 text-blue-600 mb-2" />
                  <h3 className="font-semibold group-hover:text-blue-600">Houston, TX</h3>
                </div>
              </Link>
            </div>
          </div>

          {/* FAQ Section */}
          <div className="mt-24 max-w-4xl mx-auto">
            <FAQSection
              faqs={faqs}
              title={`Frequently Asked Questions about ${service.title}`}
              description={`Get answers to common questions about ${service.title.toLowerCase()} services`}
            />
          </div>
        </div>
      </div>
    </>
  );
}